import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { cn } from '@/lib/core/utils';
import type { TourStep } from './types';
import type { WizardSummary } from '@/hooks/onboarding/useOnboardingSync';

interface TourPopoverProps {
  step: TourStep | null;
  stepIndex: number;
  totalSteps: number;
  targetRect: DOMRect | null;
  wizardSummary: WizardSummary | null;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isActive: boolean;
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 200 : -200,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 200 : -200,
    opacity: 0,
  }),
};

const springTransition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

function resolveDescription(
  desc: string | ((summary: WizardSummary | null) => string),
  summary: WizardSummary | null
): string {
  return typeof desc === 'function' ? desc(summary) : desc;
}

function computePosition(
  targetRect: DOMRect | null,
  placement: TourStep['placement']
): React.CSSProperties {
  if (!targetRect) {
    // Center on screen
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const gap = 16;
  const side = placement || 'bottom';

  switch (side) {
    case 'top':
      return {
        bottom: window.innerHeight - targetRect.top + gap,
        left: Math.max(16, Math.min(targetRect.left, window.innerWidth - 340)),
      };
    case 'left':
      return {
        top: targetRect.top,
        right: window.innerWidth - targetRect.left + gap,
      };
    case 'right':
      return {
        top: targetRect.top,
        left: targetRect.right + gap,
      };
    case 'bottom':
    default:
      return {
        top: targetRect.bottom + gap,
        left: Math.max(16, Math.min(targetRect.left, window.innerWidth - 340)),
      };
  }
}

export function TourPopover({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  wizardSummary,
  onNext,
  onPrev,
  onSkip,
  isActive,
}: TourPopoverProps) {
  const isMobile = useIsMobile();

  if (!isActive || !step) return null;

  const description = resolveDescription(step.description, wizardSummary);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  // On mobile, always show as bottom card
  const positionStyle = isMobile
    ? {}
    : computePosition(targetRect, step.placement);

  const content = (
    <AnimatePresence mode="wait" custom={1}>
      <motion.div
        key={step.id}
        className={cn(
          'fixed z-toast w-80 max-w-[calc(100vw-32px)]',
          'bg-popover text-popover-foreground border rounded-xl shadow-2xl',
          isMobile && 'bottom-0 left-0 right-0 w-full max-w-full rounded-b-none rounded-t-2xl border-b-0 pb-[env(safe-area-inset-bottom)]'
        )}
        style={isMobile ? undefined : positionStyle}
        custom={1}
        variants={isMobile ? undefined : slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={springTransition}
      >
        <div className="p-4">
          {/* Step counter */}
          <div className="flex items-center gap-1.5 mb-2">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={cn(
                  'h-1 rounded-full transition-all duration-300',
                  i === stepIndex
                    ? 'w-6 bg-primary'
                    : i < stepIndex
                    ? 'w-3 bg-primary/40'
                    : 'w-3 bg-muted-foreground/20'
                )}
              />
            ))}
            <span className="ml-auto text-xs text-muted-foreground">
              {stepIndex + 1} of {totalSteps}
            </span>
          </div>

          {/* Content */}
          <h3 className="text-base font-semibold leading-tight mb-1">
            {step.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {description}
          </p>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t">
            <Button variant="ghost"
              onClick={onSkip}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors !h-auto !p-0 rounded-none md:!text-xs hover:bg-transparent"
            >
              Skip
              <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1.5 font-mono text-2xs font-medium text-muted-foreground">
                esc
              </kbd>
            </Button>

            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button variant="outline" size="sm" onClick={onPrev} className="gap-1.5 h-8">
                  <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1.5 font-mono text-2xs font-medium text-muted-foreground">
                    ←
                  </kbd>
                  Back
                </Button>
              )}
              <Button size="sm" onClick={onNext} className="gap-1.5 h-8">
                {isLast ? 'Finish' : 'Next'}
                {!isLast && (
                  <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-primary-foreground/20 bg-primary-foreground/10 px-1.5 font-mono text-2xs font-medium text-primary-foreground">
                    →
                  </kbd>
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
